import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Grid, Button, Alert, LinearProgress,
  Stack, Chip, Divider, Tooltip, CardActionArea, Badge, TextField,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import {
  Groups as GroupsIcon,
  PlayArrow as StartIcon,
  Refresh as ReplanIcon,
  WbSunny as TomorrowIcon,
  Restaurant as RestaurantIcon,
  Person as PersonIcon,
  Warning as WarningIcon,
  Schedule as ScheduleIcon,
  ArrowForward as CarryIcon,
  CalendarMonth as CalendarIcon,
  Assignment as WorkItemsIcon,
  CheckCircle as ActiveIcon,
  ErrorOutline as OverdueIcon,
  Inbox as InboxIcon,
} from '@mui/icons-material';
import { format, addHours } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { executiveAssignmentsApi, rhythmApi, authApi, workItemsApi, calendarApi, dailyPlanApi, reportingApi, decisionsApi } from '../api/services';
import type { ExecutiveAssignment, StartMyDayResponse, RestaurantResult, WorkItem, CalendarEvent, DailyPlan, EaActivityEntry, Decision } from '../types/models';
import RestaurantSearchDialog from '../components/RestaurantSearchDialog';
import { usePrincipal } from '../context/PrincipalContext';

// Icons for new handoff UI
import {
  NoteAdd as HandoffNotesIcon,
  Send as PrepareIcon,
  OpenInNew as ViewAsIcon,
  Gavel as GavelIcon,
} from '@mui/icons-material';

interface UserInfo {
  id: string;
  email: string;
  displayName: string;
  role: string;
  isActive: boolean;
}

interface PrincipalSummary {
  planStatus: string | null; // Proposed, Approved, Active, Closed, or null for "No plan"
  overdueCount: number;
  upcomingEventsCount: number;
  eaActionCount: number; // unassigned items + pending delegations
  carryForwardCount: number;
  capacityRemainingHours: number;
  isOverloaded: boolean;
  nextMeeting: string | null;
  nextMeetingTime: string | null;
  overflowItems: WorkItem[];
}

interface ExecutiveState {
  assignment: ExecutiveAssignment;
  user: UserInfo | null;
  summary: PrincipalSummary | null;
  dayData: StartMyDayResponse | null;
  loading: boolean;
  error: string | null;
}

export default function EAWorkspacePage() {
  const navigate = useNavigate();
  const { setActivePrincipal: setGlobalPrincipal } = usePrincipal();
  const [executives, setExecutives] = useState<ExecutiveState[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [users, setUsers] = useState<UserInfo[]>([]);
  const [activePrincipalId, setActivePrincipalId] = useState<string | null>(null);
  const [restaurantDialogOpen, setRestaurantDialogOpen] = useState(false);
  const [selectedRestaurant, setSelectedRestaurant] = useState<RestaurantResult | null>(null);
  const [handoffNotes, setHandoffNotes] = useState<string>('');
  const [handoffSaving, setHandoffSaving] = useState(false);
  const [handoffSaved, setHandoffSaved] = useState(false);
  const [preparingDay, setPreparingDay] = useState(false);
  const [dayPrepared, setDayPrepared] = useState(false);
  const [eaActivity, setEaActivity] = useState<EaActivityEntry[]>([]);
  const [eaActivityLoading, setEaActivityLoading] = useState(false);
  const [queueDecisionOpen, setQueueDecisionOpen] = useState(false);
  const [queueDecisionForm, setQueueDecisionForm] = useState({ title: '', rationale: '', stakeholders: '' });
  const [queueDecisionSaving, setQueueDecisionSaving] = useState(false);
  const today = format(new Date(), 'yyyy-MM-dd');
  const tomorrow = format(addHours(new Date(), 24), 'yyyy-MM-dd');

  const handleRestaurantSelect = (restaurant: RestaurantResult) => {
    setSelectedRestaurant(restaurant);
    window.location.href = `/calendar`;
  };

  // Load summary data for a specific executive using forExecutiveUserId
  const loadPrincipalSummary = useCallback(async (execUserId: string): Promise<PrincipalSummary | null> => {
    try {
      const [todayItems, calEvents, plan] = await Promise.all([
        workItemsApi.getByDate(today, execUserId).catch(() => [] as WorkItem[]),
        calendarApi.getByDate(today, execUserId).catch(() => [] as CalendarEvent[]),
        dailyPlanApi.getByDate(today, execUserId).catch(() => null as DailyPlan | null),
      ]);

      // Overdue: items scheduled for today that are overdue (past target date, not done/cancelled)
      const todayDate = new Date(today);
      const overdueCount = todayItems.filter(i =>
        new Date(i.targetDate) < todayDate &&
        !['Done', 'Cancelled'].includes(i.status)
      ).length;

      // Today's items
      const carryForwardCount = todayItems.filter(i => i.isCarryForward).length;

      // Upcoming calendar events (next 24 hours)
      const now = new Date();
      const next24h = addHours(now, 24);
      const upcomingEventsCount = calEvents.filter(e => {
        const start = new Date(e.startTime);
        return start >= now && start <= next24h;
      }).length;

      // Next meeting
      const upcomingMeetings = calEvents
        .filter(e => !e.isAllDay && new Date(e.startTime) > now)
        .sort((a, b) => a.startTime.localeCompare(b.startTime));
      const nextMeeting = upcomingMeetings.length > 0 ? upcomingMeetings[0].title : null;
      const nextMeetingTime = upcomingMeetings.length > 0 ? upcomingMeetings[0].startTime : null;

      // EA action items: unassigned work items (no assignedToExecutiveId, still in Backlog)
      // + delegated items pending acceptance — scoped to today's items
      const eaActionCount = todayItems.filter(i =>
        (i.status === 'Backlog' && !i.assignedToExecutiveId) ||
        (i.status === 'Planned' && !i.assignedToExecutiveId)
      ).length;

      // Capacity calculation
      const meetingMinutes = calEvents
        .filter(e => !e.isAllDay)
        .reduce((sum, e) => {
          const start = new Date(e.startTime);
          const end = new Date(e.endTime);
          return sum + Math.max(0, (end.getTime() - start.getTime()) / 60000);
        }, 0);
      const plannedMinutes = todayItems
        .filter(i => !['Done', 'Cancelled'].includes(i.status))
        .reduce((sum, i) => sum + (i.estimatedMinutes || 0), 0);
      const totalWorkingMinutes = 8 * 60;
      const capacityRemainingHours = Math.max(0, Math.round((totalWorkingMinutes - meetingMinutes - plannedMinutes) / 60 * 10) / 10);
      const isOverloaded = (meetingMinutes + plannedMinutes) > totalWorkingMinutes;
      const overflowItems = isOverloaded
        ? todayItems.filter(i => !['Done', 'Cancelled'].includes(i.status)).slice(-3)
        : [];

      return {
        planStatus: plan?.status || null,
        overdueCount,
        upcomingEventsCount,
        eaActionCount,
        carryForwardCount,
        capacityRemainingHours,
        isOverloaded,
        nextMeeting,
        nextMeetingTime,
        overflowItems,
      };
    } catch {
      return null;
    }
  }, [today]);

  useEffect(() => {
    const load = async () => {
      try {
        const [assignments, usersData] = await Promise.all([
          executiveAssignmentsApi.getAll(),
          authApi.getUsers(),
        ]);
        setUsers(usersData);
        const activeAssignments = assignments.filter(a => a.isActive);
        const execStates: ExecutiveState[] = activeAssignments.map(a => ({
          assignment: a,
          user: usersData.find(u => u.id === a.executiveUserId) || null,
          summary: null,
          dayData: null,
          loading: false,
          error: null,
        }));
        setExecutives(execStates);

        // Set first principal as active by default
        if (activeAssignments.length > 0 && !activePrincipalId) {
          setActivePrincipalId(activeAssignments[0].executiveUserId);
        }

        // Load summaries in parallel
        const summaries = await Promise.all(
          activeAssignments.map(a => loadPrincipalSummary(a.executiveUserId))
        );
        setExecutives(prev => prev.map((e, i) => ({ ...e, summary: summaries[i] })));
      } catch (err: any) {
        setError(err.response?.data?.message || err.message || 'Failed to load assignments');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAction = async (index: number, action: 'start' | 'replan' | 'planTomorrow') => {
    setExecutives(prev => prev.map((e, i) => i === index ? { ...e, loading: true, error: null } : e));
    try {
      const execUserId = executives[index].assignment.executiveUserId;
      let data: StartMyDayResponse;
      if (action === 'start') {
        data = await rhythmApi.startMyDay({ forExecutiveUserId: execUserId } as any);
      } else if (action === 'replan') {
        data = await rhythmApi.replan({ forExecutiveUserId: execUserId } as any);
      } else {
        data = await rhythmApi.planTomorrow({ forExecutiveUserId: execUserId } as any);
      }
      setExecutives(prev => prev.map((e, i) => i === index ? { ...e, dayData: data, loading: false } : e));
      // Refresh summary after action
      const newSummary = await loadPrincipalSummary(execUserId);
      setExecutives(prev => prev.map((e, i) => i === index ? { ...e, summary: newSummary } : e));
    } catch (err: any) {
      setExecutives(prev => prev.map((e, i) => i === index
        ? { ...e, loading: false, error: err.response?.data?.message || err.message || 'Action failed' }
        : e
      ));
    }
  };

  const handlePrincipalClick = (execUserId: string) => {
    setActivePrincipalId(execUserId);
    setDayPrepared(false);
    setHandoffNotes('');
    setHandoffSaved(false);
  };

  /** Set the global principal context and navigate to the dashboard */
  const handleViewAsPrincipal = (execUserId: string) => {
    const exec = executives.find(e => e.assignment.executiveUserId === execUserId);
    if (exec) {
      setGlobalPrincipal({
        id: execUserId,
        name: exec.user?.displayName || 'Unknown Executive',
        email: exec.user?.email || '',
      });
      navigate('/');
    }
  };

  // Prepare Day - calls start-my-day for the executive (creates Proposed plan)
  const handlePrepareDay = async () => {
    if (!activePrincipalId || activeExecIndex < 0) return;
    setPreparingDay(true);
    try {
      const data = await rhythmApi.startMyDay({ forExecutiveUserId: activePrincipalId } as any);
      setExecutives(prev => prev.map((e, i) => i === activeExecIndex ? { ...e, dayData: data } : e));
      setDayPrepared(true);
      // Refresh summary
      const newSummary = await loadPrincipalSummary(activePrincipalId);
      setExecutives(prev => prev.map((e, i) => i === activeExecIndex ? { ...e, summary: newSummary } : e));
    } catch (err: any) {
      setExecutives(prev => prev.map((e, i) => i === activeExecIndex
        ? { ...e, error: err.response?.data?.message || err.message || 'Failed to prepare day' }
        : e
      ));
    } finally {
      setPreparingDay(false);
    }
  };

  // Save handoff notes for the current executive's plan
  const handleSaveHandoffNotes = async () => {
    if (!activePrincipalId) return;
    setHandoffSaving(true);
    try {
      await dailyPlanApi.updateHandoffNotes(today, handoffNotes, activePrincipalId);
      setHandoffSaved(true);
      setTimeout(() => setHandoffSaved(false), 3000);
    } catch (err) {
      console.error('Failed to save handoff notes', err);
    } finally {
      setHandoffSaving(false);
    }
  };

  // Load handoff notes when active exec changes
  useEffect(() => {
    if (!activePrincipalId) return;
    dailyPlanApi.getByDate(today, activePrincipalId)
      .then((plan) => {
        setHandoffNotes(plan?.handoffNotes || '');
        setDayPrepared(!!plan?.preparedByUserId);
      })
      .catch(() => {
        setHandoffNotes('');
        setDayPrepared(false);
      });
  }, [activePrincipalId, today]);

  const handleQueueDecision = useCallback(async () => {
    if (!activePrincipalId || !queueDecisionForm.title.trim()) return;
    setQueueDecisionSaving(true);
    try {
      await decisionsApi.queue({
        title: queueDecisionForm.title,
        rationale: queueDecisionForm.rationale || undefined,
        stakeholders: queueDecisionForm.stakeholders || undefined,
      }, activePrincipalId);
      setQueueDecisionOpen(false);
      setQueueDecisionForm({ title: '', rationale: '', stakeholders: '' });
    } catch (err) {
      console.error('Failed to queue decision', err);
    } finally {
      setQueueDecisionSaving(false);
    }
  }, [activePrincipalId, queueDecisionForm]);

  // Load EA activity for the active principal
  useEffect(() => {
    if (!activePrincipalId) return;
    setEaActivityLoading(true);
    const now = new Date();
    const weekAgo = new Date(now);
    weekAgo.setDate(now.getDate() - 7);
    const from = weekAgo.toISOString().slice(0, 10);
    const to = now.toISOString().slice(0, 10);
    reportingApi.getEaActivity(from, to, activePrincipalId)
      .then((data: EaActivityEntry[]) => setEaActivity(data))
      .catch(() => setEaActivity([]))
      .finally(() => setEaActivityLoading(false));
  }, [activePrincipalId]);

  // Replace raw GUIDs in activity detail text with user display names
  const resolveGuidsInDetail = (detail: string | null): string => {
    if (!detail) return '-';
    // Match standard GUID pattern
    return detail.replace(
      /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi,
      (guid) => {
        const matchedUser = users.find(u => u.id === guid);
        return matchedUser ? matchedUser.displayName : guid;
      }
    );
  };

  const formatMeetingTime = (iso: string): string => {
    try {
      return new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    } catch { return iso; }
  };

  const getPlanStatusColor = (status: string | null): 'default' | 'success' | 'warning' | 'info' | 'error' => {
    switch (status) {
      case 'Active': return 'success';
      case 'Approved': return 'info';
      case 'Proposed': return 'warning';
      case 'Closed': return 'default';
      default: return 'default';
    }
  };

  const activeExecIndex = executives.findIndex(e => e.assignment.executiveUserId === activePrincipalId);
  const activeExec = activeExecIndex >= 0 ? executives[activeExecIndex] : null;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <GroupsIcon sx={{ color: 'primary.main' }} />
        <Box>
          <Typography variant="h4">EA Workspace</Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Multi-principal dashboard — manage your assigned executives
          </Typography>
        </Box>
      </Box>

      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {!loading && executives.length === 0 && (
        <Card sx={{ p: 6, textAlign: 'center' }}>
          <GroupsIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h5" gutterBottom>No executive assignments found</Typography>
          <Typography color="text.secondary">
            Contact your admin to get assigned to an executive.
          </Typography>
        </Card>
      )}

      {/* Principal Switcher Cards */}
      {executives.length > 0 && (
        <>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
            Your Principals ({executives.length})
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {executives.map((exec) => {
              const isActive = exec.assignment.executiveUserId === activePrincipalId;
              const s = exec.summary;
              return (
                <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={exec.assignment.id}>
                  <Card
                    sx={{
                      border: 2,
                      borderColor: isActive ? 'primary.main' : 'transparent',
                      transition: 'border-color 0.2s, box-shadow 0.2s',
                      '&:hover': { borderColor: isActive ? 'primary.main' : 'action.hover' },
                    }}
                  >
                    <CardActionArea onClick={() => handlePrincipalClick(exec.assignment.executiveUserId)}>
                      <CardContent sx={{ pb: '12px !important' }}>
                        {/* Name and status */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                          <Box>
                            <Typography variant="subtitle1" fontWeight="bold" noWrap>
                              {exec.user?.displayName || 'Unknown Executive'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {exec.user?.email || ''}
                            </Typography>
                          </Box>
                          {isActive && (
                            <Chip label="Active" color="primary" size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
                          )}
                        </Box>

                        {/* Plan status */}
                        <Chip
                          label={s?.planStatus ? `Plan: ${s.planStatus}` : 'No plan'}
                          size="small"
                          color={getPlanStatusColor(s?.planStatus ?? null)}
                          variant="outlined"
                          sx={{ mb: 1.5, height: 22 }}
                        />

                        {/* Summary metrics row */}
                        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                          <Tooltip title="Overdue work items">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Badge badgeContent={s?.overdueCount ?? 0} color="error" max={99}>
                                <OverdueIcon sx={{ fontSize: 18, color: (s?.overdueCount ?? 0) > 0 ? 'error.main' : 'text.disabled' }} />
                              </Badge>
                            </Box>
                          </Tooltip>
                          <Tooltip title="Upcoming events (24h)">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Badge badgeContent={s?.upcomingEventsCount ?? 0} color="info" max={99}>
                                <CalendarIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                              </Badge>
                            </Box>
                          </Tooltip>
                          <Tooltip title="Items requiring EA action">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Badge badgeContent={s?.eaActionCount ?? 0} color="warning" max={99}>
                                <InboxIcon sx={{ fontSize: 18, color: (s?.eaActionCount ?? 0) > 0 ? 'warning.main' : 'text.disabled' }} />
                              </Badge>
                            </Box>
                          </Tooltip>
                          <Tooltip title="Capacity remaining">
                            <Chip
                              label={`${s?.capacityRemainingHours ?? '-'}h`}
                              size="small"
                              color={s?.isOverloaded ? 'error' : 'success'}
                              variant="outlined"
                              sx={{ height: 20, fontSize: '0.7rem' }}
                            />
                          </Tooltip>
                        </Box>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </>
      )}

      {/* Active Principal Detail Panel */}
      {activeExec && (
        <Card>
          <CardContent>
            {/* Executive Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box>
                <Typography variant="h6">
                  {activeExec.user?.displayName || activeExec.assignment.executiveUserId}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {activeExec.user?.email || 'Executive'}
                  {activeExec.user && (
                    <Chip label={activeExec.user.role} size="small" variant="outlined" sx={{ ml: 1 }} />
                  )}
                </Typography>
              </Box>
              <Chip
                label={activeExec.assignment.isActive ? 'Active Assignment' : 'Inactive'}
                color={activeExec.assignment.isActive ? 'success' : 'default'}
                size="small"
              />
            </Box>

            <Divider sx={{ mb: 2 }} />

            {/* Per-principal summary strip */}
            {activeExec.summary && (
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
                <Chip
                  label={activeExec.summary.planStatus ? `Plan: ${activeExec.summary.planStatus}` : 'No plan today'}
                  size="small"
                  color={getPlanStatusColor(activeExec.summary.planStatus)}
                  variant="outlined"
                />
                <Chip
                  icon={<ScheduleIcon sx={{ fontSize: 14 }} />}
                  label={activeExec.summary.nextMeeting
                    ? `Next: ${activeExec.summary.nextMeeting} at ${formatMeetingTime(activeExec.summary.nextMeetingTime!)}`
                    : 'No upcoming meetings'}
                  size="small"
                  variant="outlined"
                />
                {activeExec.summary.overdueCount > 0 && (
                  <Chip
                    icon={<OverdueIcon sx={{ fontSize: 14 }} />}
                    label={`${activeExec.summary.overdueCount} overdue`}
                    size="small"
                    color="error"
                    variant="outlined"
                  />
                )}
                {activeExec.summary.carryForwardCount > 0 && (
                  <Chip
                    icon={<CarryIcon sx={{ fontSize: 14 }} />}
                    label={`${activeExec.summary.carryForwardCount} carried forward`}
                    size="small"
                    color="warning"
                    variant="outlined"
                  />
                )}
                <Chip
                  label={`${activeExec.summary.capacityRemainingHours}h remaining`}
                  size="small"
                  color={activeExec.summary.isOverloaded ? 'error' : 'success'}
                  variant="outlined"
                />
                {activeExec.summary.eaActionCount > 0 && (
                  <Chip
                    icon={<InboxIcon sx={{ fontSize: 14 }} />}
                    label={`${activeExec.summary.eaActionCount} need EA action`}
                    size="small"
                    color="warning"
                    variant="outlined"
                  />
                )}
                {activeExec.summary.upcomingEventsCount > 0 && (
                  <Chip
                    icon={<CalendarIcon sx={{ fontSize: 14 }} />}
                    label={`${activeExec.summary.upcomingEventsCount} events (24h)`}
                    size="small"
                    variant="outlined"
                  />
                )}
                {activeExec.dayData && (
                  <Chip
                    label={`Plan: ${activeExec.dayData.plan.status}`}
                    size="small"
                    color={activeExec.dayData.plan.status === 'Active' ? 'success' : 'default'}
                    variant="outlined"
                  />
                )}
              </Box>
            )}

            {/* Prepared By Badge */}
            {dayPrepared && activeExec.summary?.planStatus && (
              <Alert severity="info" sx={{ mb: 2 }} icon={<PrepareIcon sx={{ fontSize: 20 }} />}>
                This plan was prepared by an EA and is currently in <strong>{activeExec.summary.planStatus}</strong> status.
              </Alert>
            )}

            {/* Quick Actions */}
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>Quick Actions</Typography>
            <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
              <Button
                variant="contained"
                size="small"
                color="secondary"
                startIcon={<ViewAsIcon />}
                onClick={() => handleViewAsPrincipal(activeExec.assignment.executiveUserId)}
              >
                View as {activeExec.user?.displayName?.split(' ')[0] || 'Principal'}
              </Button>
              <Button
                variant="contained"
                size="small"
                startIcon={<PrepareIcon />}
                onClick={handlePrepareDay}
                disabled={preparingDay || activeExec.loading}
                color="primary"
              >
                {preparingDay ? 'Preparing...' : 'Prepare Day'}
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<StartIcon />}
                onClick={() => handleAction(activeExecIndex, 'start')}
                disabled={activeExec.loading}
              >
                Start Their Day
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<ReplanIcon />}
                onClick={() => handleAction(activeExecIndex, 'replan')}
                disabled={activeExec.loading}
              >
                Replan
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<TomorrowIcon />}
                onClick={() => handleAction(activeExecIndex, 'planTomorrow')}
                disabled={activeExec.loading}
              >
                Plan Tomorrow
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<CalendarIcon />}
                onClick={() => navigate(`/calendar`)}
              >
                View Calendar
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<WorkItemsIcon />}
                onClick={() => navigate(`/work-items`)}
              >
                View Work Items
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<PersonIcon />}
                onClick={() => navigate(`/principal-profile/${activeExec.assignment.executiveUserId}`)}
              >
                Principal Profile
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<RestaurantIcon />}
                onClick={() => setRestaurantDialogOpen(true)}
              >
                Book Restaurant
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<GavelIcon />}
                onClick={() => setQueueDecisionOpen(true)}
                color="warning"
              >
                Queue Decision
              </Button>
            </Stack>

            {/* Handoff Notes Section */}
            {dayPrepared && (
              <Box sx={{ mb: 2, p: 2, borderRadius: 1, border: 1, borderColor: 'divider', bgcolor: 'rgba(255,255,255,0.02)' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <HandoffNotesIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                  <Typography variant="subtitle2" color="text.secondary">Handoff Notes for Executive</Typography>
                </Box>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  size="small"
                  placeholder="Add notes for the executive about today's plan, priorities, things to watch for..."
                  value={handoffNotes}
                  onChange={e => { setHandoffNotes(e.target.value); setHandoffSaved(false); }}
                />
                <Stack direction="row" spacing={1} sx={{ mt: 1 }} alignItems="center">
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleSaveHandoffNotes}
                    disabled={handoffSaving}
                  >
                    {handoffSaving ? 'Saving...' : 'Save Notes'}
                  </Button>
                  {handoffSaved && (
                    <Typography variant="caption" color="success.main">Saved</Typography>
                  )}
                </Stack>
              </Box>
            )}

            {activeExec.loading && <LinearProgress sx={{ mb: 2 }} />}
            {activeExec.error && <Alert severity="error" sx={{ mb: 2 }}>{activeExec.error}</Alert>}

            {/* Today's Overview */}
            {activeExec.dayData && (
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>Today's Overview</Typography>
                <Grid container spacing={1.5}>
                  <Grid size={{ xs: 6, sm: 3 }}>
                    <Box sx={{ p: 1.5, borderRadius: 1, bgcolor: 'rgba(255,255,255,0.03)' }}>
                      <Typography variant="caption" color="text.secondary">Available</Typography>
                      <Typography variant="h6">
                        {Math.round(activeExec.dayData.capacity.availableMinutes / 60 * 10) / 10}h
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 6, sm: 3 }}>
                    <Box sx={{ p: 1.5, borderRadius: 1, bgcolor: 'rgba(255,255,255,0.03)' }}>
                      <Typography variant="caption" color="text.secondary">Meetings</Typography>
                      <Typography variant="h6">
                        {Math.round(activeExec.dayData.capacity.meetingMinutes / 60 * 10) / 10}h
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 6, sm: 3 }}>
                    <Box sx={{ p: 1.5, borderRadius: 1, bgcolor: 'rgba(255,255,255,0.03)' }}>
                      <Typography variant="caption" color="text.secondary">Planned</Typography>
                      <Typography variant="h6">
                        {Math.round(activeExec.dayData.capacity.plannedMinutes / 60 * 10) / 10}h
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 6, sm: 3 }}>
                    <Box sx={{ p: 1.5, borderRadius: 1, bgcolor: 'rgba(255,255,255,0.03)' }}>
                      <Typography variant="caption" color="text.secondary">Utilisation</Typography>
                      <Typography variant="h6">
                        {Math.round(activeExec.dayData.capacity.utilisationPercent)}%
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
                <Box sx={{ mt: 1.5 }}>
                  <Chip
                    label={`Plan: ${activeExec.dayData.plan.status}`}
                    size="small"
                    color={activeExec.dayData.plan.status === 'Active' ? 'success' : 'default'}
                    variant="outlined"
                    sx={{ mr: 1 }}
                  />
                  {activeExec.dayData.plan.isOverloaded && (
                    <Chip label="Overloaded" size="small" color="error" variant="outlined" />
                  )}
                  {activeExec.dayData.warnings.length > 0 && (
                    <Chip label={`${activeExec.dayData.warnings.length} warnings`} size="small" color="warning" variant="outlined" sx={{ ml: 1 }} />
                  )}
                </Box>

                {/* Overflow Triage -- only when overloaded */}
                {activeExec.dayData.plan.isOverloaded && activeExec.dayData.overflowItems.length > 0 && (
                  <Box sx={{ mt: 2, p: 1.5, borderRadius: 1, border: 1, borderColor: 'error.main', bgcolor: 'rgba(244,67,54,0.04)' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                      <WarningIcon sx={{ fontSize: 16, color: 'error.main' }} />
                      <Typography variant="subtitle2" color="error.main">
                        Overflow Triage ({activeExec.dayData.overflowItems.length} items)
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      This executive's day is overloaded. The following items exceed available capacity:
                    </Typography>
                    <Stack spacing={0.5}>
                      {activeExec.dayData.overflowItems.map(item => (
                        <Box key={item.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
                          <Chip label={item.priority} size="small" variant="outlined"
                            color={item.priority === 'Critical' ? 'error' : item.priority === 'High' ? 'warning' : 'default'}
                            sx={{ height: 20, fontSize: '0.65rem' }}
                          />
                          <Typography variant="body2" sx={{ flex: 1 }}>{item.title}</Typography>
                          <Typography variant="caption" color="text.secondary">{item.estimatedMinutes}m</Typography>
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                )}
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* EA Activity Log */}
      {activeExec && (
        <Card sx={{ bgcolor: 'background.paper', mt: 2 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5 }}>
              EA Activity Log
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Actions performed by EAs on behalf of {activeExec.user?.displayName || 'this executive'} (last 7 days)
            </Typography>
            {eaActivityLoading && <LinearProgress sx={{ mb: 2 }} />}
            {!eaActivityLoading && eaActivity.length > 0 && (
              <TableContainer sx={{ maxHeight: 320 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, bgcolor: 'background.paper' }}>Time</TableCell>
                      <TableCell sx={{ fontWeight: 600, bgcolor: 'background.paper' }}>EA</TableCell>
                      <TableCell sx={{ fontWeight: 600, bgcolor: 'background.paper' }}>Action</TableCell>
                      <TableCell sx={{ fontWeight: 600, bgcolor: 'background.paper' }}>Detail</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {eaActivity.slice(0, 50).map(entry => (
                      <TableRow key={entry.id}>
                        <TableCell sx={{ whiteSpace: 'nowrap', color: 'text.secondary', fontSize: '0.8rem' }}>
                          {new Date(entry.timestamp).toLocaleString('en-GB', {
                            day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
                          })}
                        </TableCell>
                        <TableCell>{entry.eaName || 'Unknown'}</TableCell>
                        <TableCell>
                          <Chip label={entry.action} size="small" variant="outlined" sx={{ fontSize: '0.75rem' }} />
                        </TableCell>
                        <TableCell sx={{ color: 'text.secondary', fontSize: '0.8rem', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {resolveGuidsInDetail(entry.detail)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            {!eaActivityLoading && eaActivity.length === 0 && (
              <Typography color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                No EA activity recorded for this period
              </Typography>
            )}
          </CardContent>
        </Card>
      )}

      {/* Restaurant Search Dialog */}
      <RestaurantSearchDialog
        open={restaurantDialogOpen}
        onClose={() => setRestaurantDialogOpen(false)}
        onSelect={handleRestaurantSelect}
      />

      {/* Queue Decision Dialog */}
      <Dialog open={queueDecisionOpen} onClose={() => setQueueDecisionOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Queue Decision for Executive</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="info" sx={{ py: 0 }}>
              This will create a pending decision for {activeExec?.user?.displayName || 'the executive'} to approve, reject, or defer.
            </Alert>
            <TextField
              label="Decision Title"
              fullWidth
              required
              value={queueDecisionForm.title}
              onChange={e => setQueueDecisionForm(prev => ({ ...prev, title: e.target.value }))}
              placeholder="e.g. Approve Q3 budget reallocation"
            />
            <TextField
              label="Rationale"
              fullWidth
              multiline
              rows={3}
              value={queueDecisionForm.rationale}
              onChange={e => setQueueDecisionForm(prev => ({ ...prev, rationale: e.target.value }))}
              placeholder="Why is this decision needed? What are the options?"
            />
            <TextField
              label="Stakeholders"
              fullWidth
              value={queueDecisionForm.stakeholders}
              onChange={e => setQueueDecisionForm(prev => ({ ...prev, stakeholders: e.target.value }))}
              placeholder="Comma-separated names (e.g. CFO, VP Sales)"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQueueDecisionOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleQueueDecision}
            disabled={queueDecisionSaving || !queueDecisionForm.title.trim()}
            startIcon={<GavelIcon />}
          >
            {queueDecisionSaving ? 'Queueing...' : 'Queue Decision'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
